import type { ColorOptions, EdgeLabel, GraphicsInfo, SavedItem, ShapeOptions, SizeOptions } from "@/types";
import Graphic from "@arcgis/core/Graphic";
import Geometry from "@arcgis/core/geometry/Geometry";
import Point from "@arcgis/core/geometry/Point";
import Polygon from "@arcgis/core/geometry/Polygon";
import Polyline from "@arcgis/core/geometry/Polyline";
import * as geodeticAreaOperator from "@arcgis/core/geometry/operators/geodeticAreaOperator";
import * as geodeticLengthOperator from "@arcgis/core/geometry/operators/geodeticLengthOperator";
import * as geometryJsonUtils from "@arcgis/core/geometry/support/jsonUtils";
import TextSymbol from "@arcgis/core/symbols/TextSymbol";
import { activeDrawingPoint } from './map-state';

const COLORS: Record<ColorOptions, [number, number, number]> = {
    blue: [0, 0, 255],
    red: [255, 0, 0],
    green: [0, 128, 0],
    yellow: [255, 255, 0],
    hotpink: [255, 105, 180],
    cyan: [0, 255, 255],
};
const rgb = (color: ColorOptions) => COLORS[color];
const rgba = (color: ColorOptions) => [...COLORS[color], 0.4];
const lineWidth = (size: SizeOptions) => size === 'small' ? 3 : size === 'medium' ? 6 : 9;

/** Points needed before a drawing can be saved. */
export const MIN_POINTS: Record<ShapeOptions, number> = { point: 1, line: 2, polygon: 3 };

let loadingOperators: Promise<void> | undefined;

export const loadUtils = (): Promise<void> => {
    loadingOperators ??= Promise.all([
        geodeticAreaOperator.load(),
        geodeticLengthOperator.load(),
    ]).then(() => undefined).catch((error) => {
        loadingOperators = undefined;
        throw error;
    });
    return loadingOperators;
}

export const createGraphics = (color: ColorOptions, size: SizeOptions, shape: ShapeOptions, points: Point[]): GraphicsInfo => {
    if(points.length === 0){
        return {graphics: []};
    }
    switch (shape) {
        case 'point':
            return createPointGraphics(color, size, points);
        case 'line':
            return createPolylineGraphics(color, size, points);
        case 'polygon':
            return createPolygonGraphics(color, size, points);
        default:
            return {graphics: []};
    }
}

export const createParcelGraphics = (shape: Geometry): Graphic[] => [
    new Graphic({
        geometry: shape,
        symbol: {
            type: "simple-fill",
            style: 'solid',
            color: rgba('cyan'),
            outline: {
                color: rgb('cyan'),
                width: 3
            }
        }
    }),
];

/** Rebuilds the map graphics for a stored item from its persisted JSON shape. */
export const createSavedItemGraphics = (item: Pick<SavedItem, 'itemType' | 'shape' | 'color' | 'size'>): Graphic[] => {
    const { itemType, shape } = item;
    if (!shape) return [];
    const color = item.color ?? 'blue';
    const size = item.size ?? 'small';
    switch (itemType) {
        case 'point':
            return createGraphics(color, size, itemType, [geometryJsonUtils.fromJSON(shape) as Point]).graphics;
        case 'line':
        case 'polygon':
            return createGraphics(color, size, itemType, (shape as object[]).map((point) => geometryJsonUtils.fromJSON(point) as Point)).graphics;
        case 'parcel':
            return createParcelGraphics(geometryJsonUtils.fromJSON(shape) as Geometry);
        default:
            return [];
    }
}

const createPointGraphics = (color: ColorOptions, size: SizeOptions, points: Point[]): GraphicsInfo => {
    const graphics: Graphic[] = [];
    const info: GraphicsInfo = { graphics};
    const point = activeDrawingPoint(points);
    if (point) {
        const graphic = new Graphic({
            geometry: point,
            symbol: {
                type: "simple-marker",
                style: "circle",
                color: rgb(color),
                size: size === 'small' ? 6 : size === 'medium' ? 12 : 18,
            }
        });
        info.latLon = `Lat \\ Lon: (${point.latitude?.toFixed(5)}, ${point.longitude?.toFixed(5)})`;
        info.graphics.push(graphic, createLabelGraphic(point, info.latLon));
    }
    return info;
}

const createPolylineGraphics = (color: ColorOptions, size: SizeOptions, points: Point[]): GraphicsInfo => {
    if (points.length < 2) {
        return createPointGraphics(color, size, points);
    }
    const graphics: Graphic[] = [];
    const info: GraphicsInfo = { graphics};
    const polyline = new Polyline({
        paths: [points.map(point => [point.x, point.y])],
        spatialReference: points[0].spatialReference
    });
    const graphic = new Graphic({
        geometry: polyline,
        symbol: {
            type: "simple-line",
            color: rgb(color),
            style: "solid",
            width: lineWidth(size)
        }
    });
    let totalLength = 0;
    info.graphics.push(graphic);
    for (let i = 0; i < points.length - 1; i++) {
        const edgeLabel = createEdgeLabelGraphic(points[i], points[i + 1]);
        totalLength += edgeLabel.length;
        info.graphics.push(edgeLabel.graphic);
    }
    if (points.length > 2) {
        const center = polyline.extent?.center;
        if (center) {
            const graphic = createLabelGraphic(new Point({ x: center.x, y: center.y, spatialReference: points[0].spatialReference}), `Total Length: ${totalLength.toFixed(1)} ft`);
            info.graphics.push(graphic);
            info.totalLength = `Total Length: ${totalLength.toFixed(1)} ft`;
        }
    }
    return info;
}

const createPolygonGraphics = (color: ColorOptions, size: SizeOptions, points: Point[]): GraphicsInfo => {
    if (points.length < 2) {
        return createPointGraphics(color, size, points);
    }
    if (points.length === 2) {
        return createPolylineGraphics(color, size, points);
    }
    const graphics: Graphic[] = [];
    const info: GraphicsInfo = { graphics };
    const polygon = new Polygon({
        rings: [points.map(point => [point.x, point.y])],
        spatialReference: points[0].spatialReference
    });
    const graphic = new Graphic({
        geometry: polygon,
        symbol: {
            type: "simple-fill",
            color: rgba(color),
            style: "solid",
            outline: {
                color: rgb(color),
                width: lineWidth(size)
            }
        }
    });
    info.graphics.push(graphic);
    let perimeter = 0
    for (let i = 0; i < points.length - 1; i++) {
        const edgeLabel = createEdgeLabelGraphic(points[i], points[i + 1]);
        perimeter += edgeLabel.length;
        info.graphics.push(edgeLabel.graphic);
    }
    if (points.length > 2) {
        const area = getArea(polygon);
        const edgeLabel = createEdgeLabelGraphic(points.at(-1)!, points[0]);
        perimeter += edgeLabel.length;
        info.graphics.push(edgeLabel.graphic);
        info.graphics.push(createLabelGraphic(polygon, `Perimeter: ${perimeter.toFixed(1)} ft\r\nArea: ${area.toFixed(1)} acres`));
        info.totalPerimeter = `Perimeter: ${perimeter.toFixed(1)} ft`;
        info.totalArea = `Area: ${area.toFixed(1)} acres`;
    }
    return info;

}

const createEdgeLabelGraphic = (point1: Point, point2: Point): EdgeLabel => {
    const line = point1.x < point2.x ? new Polyline({
        paths: [[[point1.x, point1.y], [point2.x, point2.y]]],
        spatialReference: point1.spatialReference
    }) : new Polyline({
        paths: [[[point2.x, point2.y], [point1.x, point1.y]]],
        spatialReference: point1.spatialReference
    });
    const length = getLength(line);
    return { graphic: createLabelGraphic(line, `${length.toFixed(1)} ft`), length };
}

const getLength = (line: Polyline): number => {
    const length = geodeticLengthOperator.execute(line, { unit: 'feet', curveType: 'geodesic' });
    return length;
}

const getArea = (polygon: Polygon): number => {
    const area = geodeticAreaOperator.execute(polygon, { unit: 'acres', curveType: 'geodesic' });
    return area;
}

const createLabelGraphic = (geometry: Geometry, text: string): Graphic => {
    const isPoint = geometry.type === 'point';
    const isLine = geometry.type === 'polyline';
    const offset: number = isPoint ? 10 : 5;
    const textSymbol = new TextSymbol({
        text: text,
        color: 'black',
        font: {
            size: 12,
            family: "sans-serif"
        },
        haloSize: 1.5,
        haloColor: 'white',
        yoffset: offset,
        horizontalAlignment: 'center',
        verticalAlignment: isPoint ? 'bottom' : isLine ? 'baseline' : 'middle'
    })
    if (isLine) {
        const polyline = geometry as Polyline
        const start = polyline.paths[0][0];
        const end = polyline.paths[0].at(-1);

        if (start && end) {
            const angle = Math.atan2(start[1] - end[1], end[0] - start[0]) * 180 / Math.PI;
            textSymbol.angle = angle;
            geometry = new Point({
                x: 0.5 * (start[0] + end[0]),
                y: 0.5 * (start[1] + end[1]),
                spatialReference: geometry.spatialReference
            });
        }
    }
    return new Graphic({
        geometry: geometry,
        symbol: textSymbol
    })
}
